import { GameStatus } from './GameState.js';
import { v4 } from 'uuid';
import { buildGamePlayer } from './GamePlayer.js';
import { Pronouns } from '@ohell/common';
export const EVENT_TYPES = {
    PLAYER_KICKED: 'PLAYER_KICKED',
};
export function buildGameCmd(options, gamePlayerOptions) {
    if (!options.gameRuleSetName) {
        throw new Error('No game ruleset suggested');
    }
    if (!options.cmdType) {
        throw new Error('No game command suggested');
    }
    if (!gamePlayerOptions) {
        throw new Error('No game command player provided');
    }
    return {
        cmdId: options?.cmdId || v4(),
        gameId: options?.gameId || '',
        gameRuleSetName: options.gameRuleSetName,
        cmdType: options?.cmdType,
        player: buildGamePlayer({ ...gamePlayerOptions }),
        cmdData: options?.cmdData || {},
    };
}
export function isCmdType(cmdTypeString, cmdEnum) {
    return !!(cmdTypeString && Object.values(cmdEnum).includes(cmdTypeString));
}
export var BaseGameCmdTypes;
(function (BaseGameCmdTypes) {
    BaseGameCmdTypes["Create"] = "CREATE";
    BaseGameCmdTypes["Start"] = "START";
    BaseGameCmdTypes["Join"] = "JOIN";
    BaseGameCmdTypes["GetPlayerState"] = "GET_PLAYER_STATE";
    BaseGameCmdTypes["Exit"] = "EXIT";
    BaseGameCmdTypes["Error"] = "ERROR";
    BaseGameCmdTypes["Kick"] = "KICK";
})(BaseGameCmdTypes || (BaseGameCmdTypes = {}));
const createGame = (_prevGame, cmd) => {
    throw new Error('not implemented');
};
const joinGame = (prevGame, cmd) => {
    const cmdData = cmd.cmdData;
    const buildComputerPlayer = (cmd) => {
        const data = (cmd.cmdData || {});
        if (!data.handle) {
            throw new Error('must provide handle for computer player in cmdData');
        }
        if (!data.computerPlayerId) {
            throw new Error('must provide computerPlayerId for computer player in cmdData');
        }
        return buildGamePlayer({
            userId: v4(),
            handle: data.handle,
            pronouns: Pronouns.They,
            computerPlayerId: data.computerPlayerId,
            didExit: false,
        });
    };
    const buildHumanPlayer = (cmd) => buildGamePlayer({
        userId: cmd.player.userId,
        handle: cmd.player.handle,
        pronouns: cmd.player.pronouns,
    });
    const player = cmdData?.computerPlayerId
        ? buildComputerPlayer(cmd)
        : buildHumanPlayer(cmd);
    if (prevGame.status && prevGame.status !== GameStatus.Ready) {
        throw new Error('Game can only be joined in Ready status');
    }
    const players = [...prevGame.players];
    if (!players.find((previousPlayer) => previousPlayer.userId === player.userId)) {
        players.push(player);
    }
    const game = { ...prevGame, players };
    return { game, events: [] };
};
const getPlayerState = (prevGame, cmd) => {
    return { game: prevGame, events: [] };
};
const startGame = (prevGame, cmd) => {
    const hostUserId = cmd.player.userId;
    if (prevGame.hostUserId && prevGame.hostUserId !== hostUserId) {
        throw new Error(`Game can not be started by nonhost, host was ${prevGame.hostUserId} vs user ${hostUserId}`);
    }
    const game = { ...prevGame, status: GameStatus.InProgress };
    return { game, events: [] };
};
const buildPlayersWithExitedPlayer = (prevGame, exitingPlayerId, kickReason) => {
    const players = [...prevGame.players];
    const exitingPlayerIndex = players.findIndex((player) => player.userId === exitingPlayerId);
    if (exitingPlayerIndex !== -1) {
        const exitedPlayer = {
            ...players[exitingPlayerIndex],
            didExit: true,
            kickReason: kickReason || null,
        };
        players.splice(exitingPlayerIndex, 1, exitedPlayer);
    }
    return players;
};
const exitGame = (prevGame, cmd) => {
    const players = buildPlayersWithExitedPlayer(prevGame, cmd.player.userId, null);
    const stillPlaying = players.filter((player) => !player.didExit);
    const game = {
        ...prevGame,
        players,
        status: stillPlaying.length === 0 ? GameStatus.Abandoned : prevGame.status,
    };
    return { game, events: [] };
};
const kickPlayer = (prevGame, cmd) => {
    if (!cmd.cmdData) {
        throw new Error('Can not kick player with empty KickPlayerCmdData');
    }
    if (!cmd?.player?.userId) {
        throw new Error('Can not kick player with missing command userId');
    }
    const kickData = cmd.cmdData;
    if (cmd.player.userId !== prevGame.hostUserId) {
        throw new Error('only host can kick');
    }
    if (kickData.kickedPlayerId === prevGame.hostUserId) {
        throw new Error('Can not kick host');
    }
    const exitingUserId = kickData.kickedPlayerId;
    const players = buildPlayersWithExitedPlayer(prevGame, exitingUserId, kickData.kickReason);
    const game = {
        ...prevGame,
        players,
    };
    const playerKicked = {
        eventId: v4(),
        gameId: game.gameId,
        eventType: 'PLAYER_KICKED',
        eventData: { kickedPlayerId: exitingUserId },
    };
    return {
        game,
        events: [playerKicked],
    };
};
export const baseHandlers = {
    createGame,
    startGame,
    joinGame,
    getPlayerState,
    kickPlayer,
    exitGame,
};
//# sourceMappingURL=Game.js.map